import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useAuthStore } from '../stores/auth.ts';

/**
 * `meta.layout`  - which shell wraps the view (see App.vue).
 * `meta.auth`    - 'required' keeps signed-out visitors out, 'guest-only'
 *                  bounces signed-in users away from login/register.
 * The navigation guard is installed in `installAuthGuard` (auth phase) so this
 * module stays free of store imports and remains trivially testable.
 */
declare module 'vue-router' {
  interface RouteMeta {
    layout?: 'main' | 'auth';
    auth?: 'required' | 'guest-only';
    title?: string;
    /**
     * How much of the app shell this route gets:
     *   'feed'   — both side rails (home, and only home)
     *   'narrow' — no rails, a narrow centred column (lists)
     *   'wide'   — no rails, a roomier centred column (default)
     *   'page' — no rails; the view lays out its own columns across the
     *            container, and keeps the top bar and the mobile tab bar
     *   'full' — no rails, no padding, no tab bar (chat owns the viewport)
     */
    shell?: 'feed' | 'narrow' | 'wide' | 'page' | 'full';
    /**
     * This route's path parameters do not make it a different page.
     *
     * App.vue keys the view on route name *and* params, so a param change
     * remounts the view. That is right for `/@ploy` → `/@rin` and wrong for
     * `/messages` → `/messages/conv_1`: the second is the same inbox with a
     * conversation open, and rebuilding it threw away the list's filter, search
     * and scroll position every time one was opened or closed.
     */
    keepAcrossParams?: boolean;
    /**
     * Layout to use when nobody is signed in. The landing page is a front
     * door for a visitor and a dashboard for a member, and those want
     * different chrome.
     */
    guestLayout?: 'auth' | 'landing';
  }
}

/** Exported so the route table — and what shell each route asks for — is
 * testable without standing up the real router. */
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/Home.vue'),
    meta: { title: 'TalkVibe', shell: 'feed', guestLayout: 'landing' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/Login.vue'),
    meta: { layout: 'auth', auth: 'guest-only', title: 'Sign in' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/Register.vue'),
    meta: { layout: 'auth', auth: 'guest-only', title: 'Create your account' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/ForgotPassword.vue'),
    meta: { layout: 'auth', auth: 'guest-only', title: 'Reset your password' },
  },
  {
    // Reachable while signed out - the link arrives by email.
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/ResetPassword.vue'),
    meta: { layout: 'auth', title: 'Choose a new password' },
  },
  {
    // Deliberately not guarded: an email client may not carry the session.
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('../views/VerifyEmail.vue'),
    meta: { layout: 'auth', title: 'Confirm your email' },
  },
  {
    path: '/discover',
    name: 'discover',
    component: () => import('../views/Discover.vue'),
    meta: { auth: 'required', title: 'Discover people' },
  },
  {
    path: '/games',
    name: 'games',
    component: () => import('../views/Games.vue'),
    meta: { auth: 'required', title: 'Games' },
  },
  {
    path: '/marketplace',
    name: 'marketplace',
    component: () => import('../views/Marketplace.vue'),
    // Its own filter column beside its own results grid, so it wants the
    // container rather than the shell's centred measure.
    meta: { auth: 'required', title: 'Marketplace', shell: 'page' },
  },
  {
    // Declared before `/marketplace/:listingId` so `new` can never be read as an id.
    path: '/marketplace/new',
    name: 'listing-new',
    component: () => import('../views/CreateListing.vue'),
    // Its own form beside the card it will become, like `/pages/new`.
    meta: { auth: 'required', title: 'List something for sale', shell: 'page' },
  },
  {
    path: '/marketplace/:listingId',
    name: 'listing',
    component: () => import('../views/ListingView.vue'),
    props: true,
    // The item beside the seller: two columns the view lays out itself.
    meta: { auth: 'required', title: 'Listing', shell: 'page' },
  },
  {
    /**
     * One topic. Public like a page: a hashtag you cannot open is decoration, and the
     * whole point of painting one blue is that it goes somewhere.
     */
    path: '/tags/:tag',
    name: 'tag',
    component: () => import('../views/Tag.vue'),
    props: true,
    meta: { title: 'Topic', shell: 'narrow' },
  },
  {
    path: '/bookmarks',
    name: 'bookmarks',
    component: () => import('../views/Bookmarks.vue'),
    // One centred column of cards, like the feed it saved them from.
    meta: { auth: 'required', title: 'Bookmarks', shell: 'narrow' },
  },
  {
    path: '/friends',
    name: 'friends',
    component: () => import('../views/Friends.vue'),
    // Its own grid of cards beside its own side column, like the profile and
    // the marketplace — the shell's centred measure was squeezing it.
    meta: { auth: 'required', title: 'Friends', shell: 'page' },
  },
  {
    /**
     * The inbox, and the conversation beside it.
     *
     * One route for both panes rather than two screens: picking a
     * conversation from the list is what opening one *is*, and a page that
     * navigated away from the list to show it meant going back to pick
     * another. `conversationId` is optional so `/messages` on its own is the
     * list with nothing open.
     */
    path: '/messages/:conversationId?',
    name: 'messages',
    component: () => import('../views/Messages.vue'),
    props: true,
    // Opening a conversation is a state of this page, not a different page —
    // and on a phone the page behind it has to still be there when it closes.
    meta: { auth: 'required', title: 'Messages', keepAcrossParams: true },
  },
  {
    /**
     * A single conversation — now the inbox with that one open.
     *
     * It used to mount `Chat` on its own at `calc(100dvh - var(--header-height))`,
     * which was a second presentation of a transcript and the one place the
     * keyboard fix did not reach: `dvh` follows the URL bar and never the
     * keyboard, so the composer sat behind the keys on this route exactly as it
     * used to everywhere. Nothing in the app links here any more — the list, the
     * side rail and every notification point at `/messages/:id` — so the route
     * stays only to keep old links, bookmarks and shared URLs working, and sends
     * them to the screen that is maintained.
     */
    path: '/chat/:conversationId',
    name: 'chat',
    redirect: (to) => `/messages/${String(to.params['conversationId'] ?? '')}`,
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('../views/Notifications.vue'),
    // The same width as Settings, which it now matches in shape too: a feed
    // beside a side column. `narrow` is for a single column of reading, and
    // this stopped being one when the side column arrived.
    meta: { auth: 'required', title: 'Notifications' },
  },
  {
    // Declared before `/p/:slug` so the two cannot collide.
    path: '/pages/new',
    name: 'page-new',
    component: () => import('../views/CreatePage.vue'),
    meta: { auth: 'required', title: 'Create a page', shell: 'page' },
  },
  {
    /**
     * A page, readable by anyone.
     *
     * The only route with no `auth` requirement beyond the landing page. The
     * edge worker server-renders this URL for crawlers and for the first
     * paint; this is what takes over once Vue boots. Declared before the
     * `/@:username` catch-all so the two cannot collide.
     */
    path: '/p/:slug',
    name: 'page',
    component: () => import('../views/PageView.vue'),
    props: true,
    // Same as a profile: the view lays out its own two columns, and the
    // shell's centred column was squeezing them into half the width.
    meta: { title: 'TalkVibe', shell: 'page' },
  },
  {
    /**
     * Running a page, not reading it.
     *
     * Declared before the post route so `manage` can never be matched as a post
     * id. It is not a valid one and the view would answer 404 anyway, but relying
     * on that is relying on an id format staying what it is.
     */
    path: '/p/:slug/manage',
    name: 'page-manage',
    component: () => import('../views/PageManage.vue'),
    props: true,
    meta: { auth: 'required', title: 'Manage page', shell: 'wide' },
  },
  {
    // A post has no view of its own yet; it is the same page, and the
    // rendered HTML a crawler receives is what carries the single post.
    path: '/p/:slug/posts/:postId',
    name: 'post',
    component: () => import('../views/PageView.vue'),
    props: true,
    meta: { title: 'TalkVibe', shell: 'page' },
  },
  {
    /**
     * The groups hub — the one the nav points at, not a group.
     *
     * An exact path, so it never competes with `/groups/new` or `/groups/:id`; before
     * this existed, `/groups` fell through to the catch-all and answered 404 from a
     * link the navigation itself drew.
     */
    path: '/groups',
    name: 'groups',
    component: () => import('../views/Groups.vue'),
    // Its own directory beside the list of yours, like the marketplace.
    meta: { auth: 'required', title: 'Groups', shell: 'page' },
  },
  {
    // Declared before `/groups/:id` so the two cannot collide.
    path: '/groups/new',
    name: 'group-new',
    component: () => import('../views/CreateGroup.vue'),
    // Its own form beside its own preview, like `/pages/new`.
    meta: { auth: 'required', title: 'Create a group', shell: 'page' },
  },
  {
    // A group's feed. Members only, enforced by the API on every endpoint —
    // the route is open because authorisation lives on the server, not in a
    // router anyone can edit in their own browser.
    path: '/groups/:id',
    name: 'group',
    component: () => import('../views/GroupFeed.vue'),
    props: true,
    // The view lays out its own two columns under a full-width cover, like a profile
    // and a page do; the shell's centred measure was squeezing them into one.
    meta: { auth: 'required', title: 'Group', shell: 'page' },
  },
  {
    // Public: somebody deciding whether to sign up has to be able to read
    // these before they do.
    path: '/terms',
    name: 'terms',
    component: () => import('../views/Legal.vue'),
    props: { document: 'terms' },
    meta: { title: 'Terms of Use', shell: 'wide' },
  },
  {
    // Public, like the legal pages: somebody has to be able to read this before
    // they have an account, and the whole point of a desktop app is that it is
    // installed by people who are not signed in on that machine yet.
    path: '/download',
    name: 'download',
    component: () => import('../views/Download.vue'),
    meta: { title: 'Download', shell: 'wide' },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('../views/Legal.vue'),
    props: { document: 'privacy' },
    meta: { title: 'Privacy Policy', shell: 'wide' },
  },
  {
    path: '/moderation',
    name: 'moderation',
    component: () => import('../views/Moderation.vue'),
    // No role check here. The route is reachable, and the API answers 404 to
    // anyone who is not a moderator - so authorisation lives in exactly one
    // place, on the server, rather than in a router that anyone can edit.
    meta: { auth: 'required', title: 'Moderation' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/Settings.vue'),
    meta: { auth: 'required', title: 'Settings' },
  },
  {
    // Kept last so it never shadows a static route above.
    path: '/@:username',
    name: 'profile',
    component: () => import('../views/Profile.vue'),
    props: true,
    // No left rail: a profile is somebody else's page and lays out its own
    // two columns across the width. Your own navigation beside theirs put
    // three columns of chrome around one column of them.
    meta: { auth: 'required', title: 'Profile', shell: 'page' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFound.vue'),
    meta: { title: 'Page not found' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  /**
   * Scroll position on navigation.
   *
   * Immediate, and never smooth. This used to wait 150ms and then scroll
   * smoothly, both of which existed to stay out of the page transition's way —
   * scrolling *during* a cross-fade is what read as a stutter. There is no
   * transition now, so the delay was time spent looking at the old scroll
   * position for no reason.
   *
   * A filter change keeps its position, because only a real navigation
   * reaches here.
   */
  scrollBehavior(to, from, saved) {
    // Same page, different query (a filter, a tab): leave the scroll alone.
    if (to.name === from.name && to.path === from.path) return false;
    return saved ?? (to.hash ? { el: to.hash, top: 72 } : { top: 0 });
  },
});

/**
 * Installs the navigation guard.
 *
 * Kept as a function called from main.ts rather than run at import time,
 * because it needs Pinia to be active - and because it keeps this module
 * importable from a test without dragging the whole store graph in.
 */
export function installAuthGuard(): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore();

    // On a cold load the session is unknown until the cookie is checked.
    // Without this await, a signed-in user would be bounced to /login on
    // every refresh.
    if (!auth.ready) await auth.restore();

    if (to.meta.auth === 'required' && !auth.isAuthenticated) {
      // Remember where they were headed so login can return them there.
      return { name: 'login', query: { redirect: to.fullPath } };
    }

    if (to.meta.auth === 'guest-only' && auth.isAuthenticated) {
      return { name: 'home' };
    }

    return true;
  });
}

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · TalkVibe` : 'TalkVibe';
});
